import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DateRangePicker } from 'react-date-range';
import {
  format,
  parse,
  isValid,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { cs } from 'date-fns/locale';
import { ReactComponent as EditIcon } from 'assets/svg/2_calendar.svg';
import { Select } from 'components/Select';

import styles from 'css_modules/DateRangePicker.module.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const DATE_FORMAT = 'dd/MM/yyyy';


export const FormikDateRange = ({ valueFrom, valueTo, onChange, title }) => {
  const modalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  const [ranges, setRanges] = useState([
    { startDate: valueFrom ?? null, endDate: valueTo ?? null, key: 'selection' },
  ]);

  const [fromInput, setFromInput] = useState(valueFrom ? format(valueFrom, DATE_FORMAT) : '');
  const [toInput, setToInput] = useState(valueTo ? format(valueTo, DATE_FORMAT) : '');

  const [fromUnlimited, setFromUnlimited] = useState(false);
  const [toUnlimited, setToUnlimited] = useState(false);

  const [lastDateFrom, setLastDateFrom] = useState(valueFrom ?? null);
  const [lastDateTo, setLastDateTo] = useState(valueTo ?? null);

  const [mode, setMode] = useState('od-do');

  
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    setRanges([{ startDate: valueFrom ?? null, endDate: valueTo ?? null, key: 'selection' }]);
    setFromInput(valueFrom ? format(valueFrom, DATE_FORMAT) : '');
    setToInput(valueTo ? format(valueTo, DATE_FORMAT) : '');
    setFromUnlimited(false);
    setToUnlimited(false);
    setLastDateFrom(valueFrom ?? null);
    setLastDateTo(valueTo ?? null);
    setMode('od-do');
  }, [valueFrom, valueTo]);

  const parseDate = (val) => {
    const parsed = parse(val, DATE_FORMAT, new Date());
    return isValid(parsed) ? parsed : null;
  };

  const closeCalendar = useCallback(
    (apply = true) => {
      if (apply) {
        onChange?.(ranges[0].startDate, ranges[0].endDate);
      }
      setOpen(false);
    },
    [onChange, ranges]
  );

  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeCalendar(true);
    }
  };

  const handleSelect = (item) => {
    const sel = Object.values(item)[0];

    if (mode === 'pouze-den') {
      setRanges([{ startDate: sel.startDate, endDate: sel.startDate, key: 'selection' }]);
      setFromInput(format(sel.startDate, DATE_FORMAT));
      setToInput(format(sel.startDate, DATE_FORMAT));
    } else if (mode === 'neomezeno-od') {
      setRanges([{ startDate: null, endDate: sel.endDate, key: 'selection' }]);
      setToInput(sel.endDate ? format(sel.endDate, DATE_FORMAT) : '');
    } else if (mode === 'neomezeno-do') {
      setRanges([{ startDate: sel.startDate, endDate: null, key: 'selection' }]);
      setFromInput(sel.startDate ? format(sel.startDate, DATE_FORMAT) : '');
    } else {
      setRanges([{ ...sel, key: 'selection' }]);
      setFromInput(sel.startDate ? format(sel.startDate, DATE_FORMAT) : '');
      setToInput(sel.endDate ? format(sel.endDate, DATE_FORMAT) : '');
    }
  };

  const handleFromChange = (val) => {
    setFromInput(val);
    if (val.length !== 10) return;
    const parsed = parseDate(val);
    if (!parsed) return;
    setRanges([{ startDate: parsed, endDate: ranges[0].endDate, key: 'selection' }]);
  };

  const handleToChange = (val) => {
    setToInput(val);
    if (val.length !== 10) return;
    const parsed = parseDate(val);
    if (!parsed) return;
    setRanges([{ startDate: ranges[0].startDate, endDate: parsed, key: 'selection' }]);
  };

  const selectOptions = [
    { id: 'od-do', title: 'Od–Do' },
    { id: 'pouze-den', title: 'Pouze vybraný den' },
    { id: 'neomezeno-od', title: 'Neomezeno od' },
    { id: 'neomezeno-do', title: 'Neomezeno do' },
  ];

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setMode(newMode);

    switch (newMode) {
      case 'od-do':
        setFromUnlimited(false);
        setToUnlimited(false);
        setFromInput(lastDateFrom ? format(lastDateFrom, DATE_FORMAT) : '');
        setToInput(lastDateTo ? format(lastDateTo, DATE_FORMAT) : '');
        setRanges([{ startDate: lastDateFrom, endDate: lastDateTo, key: 'selection' }]);
        break;

      case 'pouze-den':
        setFromUnlimited(false);
        setToUnlimited(false);
        break;

      case 'neomezeno-od':
        setFromUnlimited(true);
        setToUnlimited(false);
        setFromInput('');
        setRanges([{ startDate: null, endDate: ranges[0].endDate, key: 'selection' }]);
        break;

      case 'neomezeno-do':
        setFromUnlimited(false);
        setToUnlimited(true);
        setToInput('');
        setRanges([{ startDate: ranges[0].startDate, endDate: null, key: 'selection' }]);
        break;

      default:
        setFromUnlimited(false);
        setToUnlimited(false);
        setFromInput(lastDateFrom ? format(lastDateFrom, DATE_FORMAT) : '');
        setToInput(lastDateTo ? format(lastDateTo, DATE_FORMAT) : '');
        setRanges([{ startDate: lastDateFrom, endDate: lastDateTo, key: 'selection' }]);
        break;
    }
  };

  const presets = [
    { label: 'Dnes', range: () => ({ startDate: new Date(), endDate: new Date() }) },
    {
      label: 'Tento týden',
      range: () => ({
        startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
        endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
      }),
    },
    {
      label: 'Minulý týden',
      range: () => ({
        startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
        endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      }),
    },
    {
      label: 'Tento měsíc',
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      }),
    },
    {
      label: 'Minulý měsíc',
      range: () => ({
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1)),
      }),
    },
    {
      label: 'Tento kvartál',
      range: () => {
        const d = subQuarters(new Date(), 0);
        return { startDate: startOfQuarter(d), endDate: endOfQuarter(d) };
      },
    },
    {
      label: 'Minulý kvartál',
      range: () => {
        const d = subQuarters(new Date(), 1);
        return { startDate: startOfQuarter(d), endDate: endOfQuarter(d) };
      },
    },
    {
      label: 'Tento rok',
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
      }),
    },
  ];

  const openCalendar = () => {
    setSnapshot({
      ranges: ranges[0],
      fromInput,
      toInput,
      fromUnlimited,
      toUnlimited,
      lastDateFrom,
      lastDateTo,
      mode,
    });
    setOpen(true);
  };

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper} onClick={openCalendar}>
        <input
          readOnly
          className={styles.input}
          placeholder="Vyberte časový úsek"
          value={
            ranges[0].startDate || ranges[0].endDate
              ? `${ranges[0].startDate ? format(ranges[0].startDate, DATE_FORMAT) : '–'} – ${
                  ranges[0].endDate ? format(ranges[0].endDate, DATE_FORMAT) : '–'
                }`
              : ''
          }
        />
        <EditIcon className={styles.calendarIcon} />
      </div>

      {open &&
        createPortal(
          <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div ref={modalRef} className={styles.calendarPopup}>
              <div className={styles.presetsColumn}>
                <div className={styles.presetTitle}>Vyber časový úsek</div>

                {presets.map((p) => (
                  <button
                    type="button"
                    key={p.label}
                    className={styles.presetItem}
                    onClick={() => {
                      const r = p.range();
                      setRanges([{ ...r, key: 'selection' }]);
                      setFromInput(format(r.startDate, DATE_FORMAT));
                      setToInput(format(r.endDate, DATE_FORMAT));
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className={styles.calendarWrapper}>
                {title && <div className={styles.modalMainTitle}>{title}</div>}

                <div className={styles.toggleCheckboxes}>
                  <div className={styles.toggleItem}>
                    <Select
                      data={selectOptions}
                      value={mode}
                      onChange={handleModeChange}
                      inputClass={styles.selectMode}
                      noBlankOption
                      onlyvalid
                    />
                    <div className={styles.dateInputWrapper}>
                      <input
                        className={styles.dateBox}
                        value={fromInput}
                        onChange={(e) => handleFromChange(e.target.value)}
                        placeholder="---"
                        disabled={mode === 'neomezeno-od'}
                      />
                      <EditIcon className={styles.dateIcon} />
                    </div>
                    <div className={styles.dateInputWrapper}>
                      <input
                        className={styles.dateBox}
                        value={toInput}
                        onChange={(e) => handleToChange(e.target.value)}
                        placeholder="---"
                        disabled={mode === 'neomezeno-do'}
                      />
                      <EditIcon className={styles.dateIcon} />
                    </div>
                  </div>
                </div>

                <DateRangePicker
                  ranges={ranges}
                  onChange={handleSelect}
                  showDateDisplay={false}
                  rangeColors={['var(--primary)']}
                  inputRanges={[]}
                  definedRanges={[]}
                  months={2}
                  direction="horizontal"
                  moveRangeOnFirstSelection={false}
                  locale={cs}
                />

                <div className={styles.calendarActions}>
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => {
                      if (snapshot) {
                        setRanges([{ ...snapshot.ranges, key: 'selection' }]);
                        setFromInput(snapshot.fromInput);
                        setToInput(snapshot.toInput);
                        setFromUnlimited(snapshot.fromUnlimited);
                        setToUnlimited(snapshot.toUnlimited);
                        setLastDateFrom(snapshot.lastDateFrom);
                        setLastDateTo(snapshot.lastDateTo);
                        setMode(snapshot.mode);
                      }
                      setOpen(false);
                    }}
                  >
                    Zrušit
                  </button>

                  <button
                    type="button"
                    className={styles.applyButton}
                    onClick={() => closeCalendar(true)}
                  >
                    Potvrdit
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};